import { useState, useContext, useCallback } from "react";
import axios from "axios";
import { GlobalContext } from "../../Context/GlobalContext";

const useLogin = () => {
  const { baseUrl, basicAuth } = useContext(GlobalContext);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loginWithPasswordApi = useCallback(
    async (mobile, password, latitude, longitude) => {
      try {
        const response = await axios.post(
          `${baseUrl}/User/v2.0/userLogin`,
          {
            mobile,
            password,
            latitude: latitude || "",
            longitude: longitude || "",
          },
          {
            headers: {
              Authorization: basicAuth,
              "Content-Type": "application/json",
            },
          },
        );

        return response.data;
      } catch (err) {
        setError(err);
        return null;
      }
    },
    [baseUrl, basicAuth],
  );

  const sendOtpLogin = useCallback(
    async (mobile, password) => {
      try {
        const response = await axios.post(
          `${baseUrl}/User/v1/userLogin`,
          {
            mobile,
            password,
            step: "1",
            otp: "",
            latitude: "",
            longitude: "",
          },
          {
            headers: {
              Authorization: basicAuth,
              "Content-Type": "application/json",
            },
          },
        );

        return response.data;
      } catch (err) {
        setError(err);
        return null;
      }
    },
    [baseUrl, basicAuth],
  );

  const verifyOtpLogin = useCallback(
    async (mobile, password, otp) => {
      try {
        const response = await axios.post(
          `${baseUrl}/User/v1/userLogin`,
          {
            mobile,
            password,
            step: "2",
            otp,
            latitude: "",
            longitude: "",
          },
          {
            headers: {
              Authorization: basicAuth,
              "Content-Type": "application/json",
            },
          },
        );

        return response.data;
      } catch (err) {
        setError(err);
        return null;
      }
    },
    [baseUrl, basicAuth],
  );

  const resendOtpLogin = useCallback(
    async (mobile, password, otp, latitude, longitude) => {
      try {
        const response = await axios.post(
          `${baseUrl}/User/v1/userLogin`,
          {
            mobile,
            password,
            step: 1,
            otp,
            latitude,
            longitude,
          },
          {
            headers: {
              Authorization: basicAuth,
              "Content-Type": "application/json",
            },
          },
        );

        return response.data;
      } catch (err) {
        setError(err);
        return null;
      }
    },
    [baseUrl, basicAuth],
  );

  const sendForgetOtpApi = useCallback(
    async (mobile) => {
      try {
        const response = await axios.post(
          `${baseUrl}/User/v1.0/forgetPassword`,
          {
            mobile,
            newPassword: "",
            otp: "",
            step: "1",
          },
          {
            headers: {
              Authorization: basicAuth,
              "Content-Type": "application/json",
            },
          },
        );

        return response.data;
      } catch (err) {
        setError(err);
        return null;
      }
    },
    [baseUrl, basicAuth],
  );

  const verifyForgetOtpApi = useCallback(
    async (mobile, otp) => {
      try {
        const response = await axios.post(
          `${baseUrl}/User/v1.0/forgetPassword`,
          {
            mobile,
            newPassword: "",
            otp,
            step: "2",
          },
          {
            headers: {
              Authorization: basicAuth,
              "Content-Type": "application/json",
            },
          },
        );

        return response.data;
      } catch (err) {
        setError(err);
        return null;
      }
    },
    [baseUrl, basicAuth],
  );

  const resetPasswordApi = useCallback(
    async (mobile, newPassword, otp) => {
      try {
        const response = await axios.post(
          `${baseUrl}/User/v1.0/forgetPassword`,
          {
            mobile,
            newPassword,
            otp,
            step: "3",
          },
          {
            headers: {
              Authorization: basicAuth,
              "Content-Type": "application/json",
            },
          },
        );

        return response.data;
      } catch (err) {
        setError(err);
        return null;
      }
    },
    [baseUrl, basicAuth],
  );

  const registerWithGoogle = useCallback(
    async (googleUser) => {
      try {
        const response = await fetch(`${baseUrl}/User/register`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: basicAuth,
          },
          body: JSON.stringify({
            userid: "1",
            clientName: googleUser.name,
            password: googleUser.email,
            clientNumber: googleUser.email,
            userType: "1",
            onboardingType: "2",
            onboardedBy: "1",
            clientId: "1",
            billingName: googleUser.name,
            allowedIp: "",
            firstName: googleUser.firstName,
            lastName: googleUser.lastName,
          }),
        });

        const result = await response.json();

        const message = result?.responseMessage?.toLowerCase() || "";

        const isNewUser =
          message.includes("created") || message.includes("new user");

        if (result?.responseCode === "00" && isNewUser) {
          sendNewUserEmail({
            email: googleUser.email,
            username: googleUser.name,
          });
        }

        return result;
      } catch (err) {
        setError(err);
        return null;
      }
    },
    [baseUrl, basicAuth],
  );

  const sendNewUserEmail = async (user) => {
    const emailToSend = user?.email || user?.clientNumber || user?.username;

    if (!emailToSend) return;

    const payload = {
      to: [emailToSend],
      keyid: "7a57255fbb9021f1ac3a52010aa0680h",
      keySecret:
        "1edf18a65735bc6560d62d12ea82b9d28b342157a345654eb5ed8eab2addc890",
      triggerId: "TR-ce6a7222e2e211f0ac3a42010aa0000f",
      variables: {
        user_name: user?.username || user?.name,
        login_link: "https://isancharr.com/",
      },
    };

    try {
      await fetch("https://api.shubhpaisa.com/api/ShubhPaisa/SendEmail", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    } catch (err) {
      console.error("EMAIL API ERROR:", err);
    }
  };

  return {
    loading,
    error,
    loginWithPasswordApi,
    sendOtpLogin,
    verifyOtpLogin,
    resendOtpLogin,
    sendForgetOtpApi,
    verifyForgetOtpApi,
    resetPasswordApi,
    registerWithGoogle,
  };
};

export default useLogin;
